import {
  Grid,
  withStyles,
  WithStyles,
  withWidth,
  Theme,
  CircularProgress
} from '@material-ui/core'
import { WithWidth } from '@material-ui/core/withWidth'
import arrayMove from 'array-move'
import classNames from 'classnames'
import { ObjectFitProperty } from 'csstype'
import i18n from 'i18next'
import { Component, Fragment, useState, useEffect } from 'react'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import deleteIcon from '../../../images/deleteIcon.svg'
import { isSmallScreen } from '../../../libs/screenSize'
import { BucketObject } from '../../../services/storage/types'
import { ApartmentMedia, ApartmentMediaItem } from '../../../types/apartments'
import { ImageFileWithObjectURL } from '../../../types/files'


const styles = ({ breakpoints }: Theme) => {
  const hasSteps = window.location.href.includes("steps");

  return {
    root: {
      maxWidth: '100%',
      padding: '10px 0px 0px 0px'
    },
    rightArrow: {
      display: 'block',
      background: 'grey',
      borderRadius: '40px'
    },
    leftArrow: {
      display: 'block',
      background: 'grey',
      borderRadius: '40px'
    },
    removeIcon: {
      zIndex: 99998,
      position: 'absolute' as const,
      top: '-5px',
      left: '-5px',
      height: '50px',
      width: '50px',
      borderRadius: '50%',
      background: '#fff',
      cursor: 'pointer',
      '& > svg, & > img': {
        zIndex: 99999,
        position: 'relative' as const,
        top: '5px',
        left: '5px'
      }
    },
    moveIcon: {
      zIndex: 99998,
      position: 'absolute' as const,
      height: '50px',
      width: '50px',
      borderRadius: '50%',
      background: '#fff',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative' as const,
        background: '#FCF3F5',
        borderRadius: '50%',
        width: '38px',
        height: '38px',
        transition: 'opacity 0.2s ease'
      }
    },
    moveIconUp: {
      top: '-5px',
      right: '-5px'
    },
    moveIconDown: {
      bottom: '-5px',
      right: '-5px'
    },
    moveIconLeft: {
      bottom: '-5px',
      left: '-5px'
    },
    moveIconRight: {
      bottom: '-5px',
      right: '-5px'
    },
    moveIconDisabled: {
      cursor: 'default',
      pointerEvents: 'none',
      '& > div': {
        opacity: 0.4
      }
    },
    imageContainer: {
      position: 'relative' as const,
      width: '100%',
    },
    gallery: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '12px',
      overflowX: 'hidden',
      overflowY: 'visible',
      justifyContent: 'flex-start',
      maxWidth: '100%',
      [breakpoints.down('xs')]: {
        height: 'auto', // don't set fixed height unless calculated
        flexWrap: 'wrap',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'auto',         // ✅ allow vertical scroll on mobile
        WebkitOverflowScrolling: 'touch' // ✅ smooth scroll on iOS
      },
      '&::-webkit-scrollbar': {
        height: '20px',
        color: 'black'
      },
      '&::-webkit-scrollbar-track': {
        boxShadow: 'inset 0 0 5px #e8f6e5',
        borderRadius: '10px'
      },

      '&::-webkit-scrollbar-thumb': {
        background: '#94004A',
        borderRadius: '10px'
      },

      '&::-webkit-scrollbar-thumb:hover': {
        background: '#670033'
      }
    },
    thumbnail: {
      height: '150px',          // <-- force height for uniform size
      width: '180px',             // <-- force width for uniform size
      borderRadius: '16px',
      objectFit: 'cover',         // <-- ensures image fills square box
      [breakpoints.down('xs')]: {
        height: '250px',
        width: '100%',
        maxWidth: '100%'
      }
    },
    itemContainer: {
      margin: '6px',
      width: '180px',
      flex: '0 0 auto',
      position: 'relative' as const,
      padding: '10px 0',
      zIndex: hasSteps ? 1200 : 99999,
      boxSizing: 'border-box',
      [breakpoints.down('xs')]: {
        width: '97%',
        userSelect: 'none',
      }
    }
  };
};


export const isBucketObject = (
  file: BucketObject | ImageFileWithObjectURL
): file is BucketObject => (file as BucketObject)?.url !== undefined

type MediaFile = BucketObject | ImageFileWithObjectURL

const MoveButton = ({
  classes,
  onClick,
  disabled,
  IconComponent,
  positionClass
}: {
  classes: any
  onClick: () => void
  disabled: boolean
  IconComponent: typeof ArrowDownwardIcon
  positionClass: string
}) => (
  <span
    className={classNames(
      classes.moveIcon,
      positionClass,
      disabled ? classes.moveIconDisabled : undefined
    )}
    onClick={() => {
      if (!disabled) {
        onClick()
      }
    }}
  >
    <div>
      <IconComponent fontSize='small' />
    </div>
  </span>
)

const ImageComponent = ({
  classes,
  file,
  selectKey
}: {
  classes: any
  file: any
  selectKey: any
}) => (
  <img
    className={classNames(classes.thumbnail)}
    src={isBucketObject(file) ? file.url : file.key}
    alt={isBucketObject(file) ? file.key : file.path}
    onClick={() => {
      selectKey(file.key)
    }}
  />
)

const MediaItem = withStyles(styles)(
  ({
    file,
    classes,
    selectKey,
    withDelete,
    t,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
    disableActions,
    useVerticalArrows
  }: {
    file: MediaFile
    classes: any
    selectKey: (key: string) => void
    withDelete: any
    t: i18n.TFunction
    onMoveUp: () => void
    onMoveDown: () => void
    isFirst: boolean
    isLast: boolean
    disableActions: boolean
    useVerticalArrows: boolean
  }) => {
    const [isDeleting, setIsDeleting] = useState(false)

    const handleDelete = async () => {
      if (sessionStorage.getItem('isDeletingImage') === 'true') {
        return
      }
      setIsDeleting(true)
      sessionStorage.setItem('isDeletingImage', 'true')

      try {
        await withDelete.deleteMedia(file.key)
      } catch (error) {
        console.error('Error deleting media:', error)
      } finally {
      }
    }

    const UpIcon = useVerticalArrows ? ArrowUpwardIcon : ArrowBackIcon
    const DownIcon = useVerticalArrows ? ArrowDownwardIcon : ArrowForwardIcon
    const upPositionClass = useVerticalArrows
      ? classes.moveIconUp
      : classes.moveIconLeft
    const downPositionClass = useVerticalArrows
      ? classes.moveIconDown
      : classes.moveIconRight

    return (
      <div className={classes.itemContainer}>
        <ImageComponent
          classes={classes}
          file={file}
          selectKey={selectKey}
        />
        <span className={classes.removeIcon} onClick={handleDelete}>
          {isDeleting ? (
            <CircularProgress
              size={28}
              style={{
                color: '#fff',
                margin: '6px 0 0 6px',
                background: '#f44f50',
                borderRadius: '100px',
                padding: '5px'
              }}
            />
          ) : (
            <img src={deleteIcon} />
          )}
        </span>
        <MoveButton
          classes={classes}
          onClick={onMoveUp}
          disabled={disableActions || isFirst}
          IconComponent={UpIcon}
          positionClass={upPositionClass}
        />
        <MoveButton
          classes={classes}
          onClick={onMoveDown}
          disabled={disableActions || isLast}
          IconComponent={DownIcon}
          positionClass={downPositionClass}
        />
      </div>
    )
  }
)

const MediaList = withStyles(styles)(
  ({
    files,
    selectedKey,
    selectKey,
    classes,
    withDelete,
    t,
    useVerticalArrows,
    onMoveUp,
    onMoveDown
  }: {
    files: MediaFile[]
    selectedKey: string
    selectKey: (key: string) => void
    classes: any
    withDelete: any
    t: i18n.TFunction
    useVerticalArrows: boolean
    onMoveUp: (index: number) => void
    onMoveDown: (index: number) => void
  }) => {
    const [isDeletingImage, setIsDeletingImage] = useState(
      sessionStorage.getItem('isDeletingImage') === 'true'
    )

    useEffect(() => {
      const handleStorageChange = () => {
        setIsDeletingImage(sessionStorage.getItem('isDeletingImage') === 'true')
      }

      window.addEventListener('storage', handleStorageChange)

      return () => {
        window.removeEventListener('storage', handleStorageChange)
      }
    }, [])

    return (
      <Grid className={classes.gallery}>
        {files.map((file: MediaFile, index: number) => (
          <MediaItem
            key={file.key}
            file={file}
            selectKey={selectKey}
            withDelete={withDelete}
            t={t}
            onMoveUp={() => onMoveUp(index)}
            onMoveDown={() => onMoveDown(index)}
            isFirst={index === 0}
            isLast={index === files.length - 1}
            disableActions={isDeletingImage}
            useVerticalArrows={useVerticalArrows}
          />
        ))}
      </Grid>
    )
  }
)

interface MediaProps extends WithStyles, WithWidth {
  media: ApartmentMedia
  files: MediaFile[] | undefined
  withDelete?: { deleteMedia: (arg: any) => any }
  reorderMedia: (arg: any) => any
  slidesToShow?: number
  t: i18n.TFunction
  /** css height property */
  imageHeight?: string
  /** css width property */
  imageWidth?: string
  /**  objectFit property */
  imageFit?: ObjectFitProperty
}

interface MediaState {
  selectedKey: string
  media: ApartmentMedia
}

class MediaGallery extends Component<MediaProps, MediaState> {
  constructor(props: MediaProps) {
    super(props)
    this.state = {
      media: props.media,
      selectedKey:
        (Array.isArray(props.media) && props.media[0] && props.media[0].key) ||
        ''
    }
  }

  componentDidMount() {
    sessionStorage.removeItem('isDeletingImage')
  }

  handleMoveUp = (oldIndex: number) => {
    const { reorderMedia, files } = this.props
    const { media } = this.state

    if (!Array.isArray(media) || oldIndex <= 0) {
      return
    }

    // Filter media to only include items that exist in files (S3)
    const normalize = (k: string = '') =>
      k.trim().toLowerCase().replace(/^https?:\/\/[^/]+\/+/, '')
    
    const validMedia = media.filter(m => 
      files?.some(file => normalize(file.key) === normalize(m.key))
    )

    // Find the actual index in the full media array
    const actualOldIndex = media.findIndex(m => m.key === validMedia[oldIndex].key)
    const actualNewIndex = media.findIndex(m => m.key === validMedia[oldIndex - 1].key)

    const updatedMedia = arrayMove<ApartmentMediaItem>(media, actualOldIndex, actualNewIndex)

    this.setState({ media: updatedMedia })
    reorderMedia({ oldIndex: actualOldIndex, newIndex: actualNewIndex })
  }

  handleMoveDown = (oldIndex: number) => {
    const { reorderMedia, files } = this.props
    const { media } = this.state

    if (!Array.isArray(media) || oldIndex >= media.length - 1) {
      return
    }

    // Filter media to only include items that exist in files (S3)
    const normalize = (k: string = '') =>
      k.trim().toLowerCase().replace(/^https?:\/\/[^/]+\/+/, '')
    
    const validMedia = media.filter(m => 
      files?.some(file => normalize(file.key) === normalize(m.key))
    )

    // Check if we're at the last valid item
    if (oldIndex >= validMedia.length - 1) {
      return
    }

    // Find the actual index in the full media array
    const actualOldIndex = media.findIndex(m => m.key === validMedia[oldIndex].key)
    const actualNewIndex = media.findIndex(m => m.key === validMedia[oldIndex + 1].key)

    const updatedMedia = arrayMove<ApartmentMediaItem>(media, actualOldIndex, actualNewIndex)

    this.setState({ media: updatedMedia })
    reorderMedia({ oldIndex: actualOldIndex, newIndex: actualNewIndex })
  }

  slideAmount = (slidesToShow?: number) => slidesToShow || 1

  slidesToShow = () => {
    const slideAmount = this.slideAmount(this.props.slidesToShow)
    const isNormalScreen = !isSmallScreen(this.props.width)
    const slidesToShow =
      this.props.files &&
        this.props.files.length >= slideAmount &&
        isNormalScreen
        ? slideAmount
        : 1

    return slidesToShow
  }

  componentDidUpdate(prevProps: MediaProps) {
    // Always sync state with props when media changes from Redux
    if (this.props.media !== prevProps.media) {
      this.setState({ media: this.props.media });
    }
  }

  render() {
    const {
      files,
      // slidesToShow,
      withDelete,
      reorderMedia,
      t,
      classes
    } = this.props
    const smScreen = window.innerWidth < 600;
    const { media } = this.state
    const selectedKey = this.state.selectedKey || (media && media[0].key) || ''
    let sortedFiles: (BucketObject | ImageFileWithObjectURL)[] = []
    
    // Reorder files based on media array order (media index = sequence)
    if (Array.isArray(media) && Array.isArray(files)) {
      const normalize = (k: string = '') =>
        k.trim().toLowerCase()

      // Map through media array in order, find matching file, skip if not found
      sortedFiles = media
        .map((m) => {
          const found = files.find(
            (file) => normalize(file.key) === normalize(m.key)
          )
          return found
        })
        .filter((file): file is BucketObject | ImageFileWithObjectURL => file !== undefined)
    }
    return (
      <Fragment>
        {files && files.length > 0 && (
          <Grid className={classes.root}>
            {/* <Grid className={classes.imageContainer}>
              {withDelete && (
                <Tooltip title={`${t('apartment.mediaUpload.delete')}`}>
                  <span
                    className={classes.removeIcon}
                    onClick={() => {
                      withDelete && withDelete.deleteMedia(selectedKey)
                      this.setState({
                        selectedKey: ''
                      })
                    }}
                  >
                    <DeleteCircleIcon size={40} color='red' />
                  </span>
                </Tooltip>
              )}
              <img
                style={{
                  maxHeight: '360px',
                  height: imageHeight || undefined,
                  width: imageWidth || '100%',
                  margin: '0 auto',
                  objectFit: imageFit || 'contain'
                }}
                className={classes.image}
                src={
                  isBucketObject(
                    selectedFile as BucketObject | ImageFileWithObjectURL
                  )
                    ? (selectedFile as BucketObject).url
                    : selectedFile.key
                }
                alt={
                  isBucketObject(selectedFile)
                    ? selectedFile.key
                    : selectedFile.path
                }
              />
            </Grid> */}
            <Grid>
              <MediaList
                files={sortedFiles}
                selectedKey={selectedKey}
                withDelete={withDelete}
                t={t}
                selectKey={(key) => {
                  this.setState({
                    selectedKey: key
                  })
                }}
                useVerticalArrows={smScreen}
                onMoveUp={this.handleMoveUp}
                onMoveDown={this.handleMoveDown}
              />
            </Grid>
          </Grid>
        )}
      </Fragment>
    )
  }
}

export default withStyles(styles)(withWidth()(MediaGallery))
